import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { newIdempotencyKey } from "@/lib/idempotency";
import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { StableInput } from "@/components/StableField";
import { usePersistedBoolean } from "@/lib/usePersistedBoolean";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BookOpen, ChevronDown, ChevronRight, Link2, Trash2, Ban, RotateCcw, SlidersHorizontal } from "lucide-react";
import {
  TagAssignmentPopover,
  TagFilter,
  TagList,
  useEntityTags,
} from "@/components/TagComponents";
import { DocumentReferenceFields, DocumentBadge, useDocumentTypes } from "@/components/DocumentComponents";
import {
  arCancelOccurrence,
  arCreateCollection,
  arDeleteReceivable,
  arGetOccurrence,
  arListCategories,
  arListClients,
  arListOccurrences,
  arListReceivables,
  arMarkOccurrenceNotDue,
  arRestoreOccurrence,
} from "@/lib/ar-api";
import { listPaymentMethods } from "@/lib/api";
import {
  arMethodIT,
  arMethodOptions,
  arStatusIT,
} from "@/lib/ar-format";
import {
  EUR,
  fmtDate,
  isoLocalDate,
  monthBounds,
  num,
  yearBounds,
} from "@/lib/format";

/**
 * v1.60.4 — Mappa il nome di un PaymentMethodDef (utente-editabile in
 * Impostazioni) verso l'enum `CollectionMethod` che il backend accetta
 * sul POST /api/ar/collections. Match case + accent-insensitive su
 * frammenti di nome (es. "RID/SDD" → "sdd", "Contanti/Cassa" → "cash").
 *
 * Ritorna null se non c'è un match evidente: il chiamante userà il
 * default "bank_transfer".
 */
function pmNameToCollectionMethod(name) {
  if (!name) return null;
  const n = String(name)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (n.includes("sdd") || n.includes("rid")) return "sdd";
  if (n.includes("bonific")) return "bank_transfer";
  if (n.includes("contant") || n.includes("cassa")) return "cash";
  if (n.includes("pos") || n.includes("carta")) return "card";
  return "other";
}

/**
 * Scadenze incassi — operational landing page for the receivables module.
 *
 * Mirrors /occurrences (payments) but for client receivables. Uses the
 * pre-built /api/ar/* backend. Ordinary vs recurring is exposed as a filter
 * (recurrence type 'one_time' = ordinary), and dedicated buttons let users
 * jump to the recurring/ordinary list pages.
 */
export default function Receivables() {
  const today = new Date();
  const [filters, setFilters] = useState(() => {
    // v1.60.4 — apertura su MESE CORRENTE (in linea con "Scadenzario Spese"
    // per operatività quotidiana). L'utente può allargare a anno/tutto
    // dai preset o dal PeriodPicker.
    const [from, to] = monthBounds(today);
    return {
      q: "",
      date_from: from,
      date_to: to,
      client_id: "",
      category_id: "",
      status: "",
      kind: "all", // all | ordinary | recurring
    };
  });

  // v1.60.4 — di default nascondi le scadenze già chiuse (incassate,
  // annullate, non dovute). Toggle per mostrarle tutte quando serve un
  // audit storico. Persistito in localStorage così la scelta operativa
  // non si perde tra i cambi di pagina.
  const [showCollected, setShowCollected] = usePersistedBoolean(
    "receivables:showCollected",
    false,
  );

  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick collect dialog state
  const [collectRow, setCollectRow] = useState(null);
  const [collectAmount, setCollectAmount] = useState("");
  const [collectDate, setCollectDate] = useState(() => isoLocalDate(today));
  const [collectMethod, setCollectMethod] = useState("bank_transfer");
  const [collectRef, setCollectRef] = useState("");
  // Iter41: dati fattura — vengono applicati all'occurrence (non al singolo
  // incasso). Precaricati con i valori esistenti se la scadenza ha già
  // dati fattura (es. incasso parziale precedente o ordinario fatturato).
  const [collectInvoiceRef, setCollectInvoiceRef] = useState("");
  const [collectInvoiceDate, setCollectInvoiceDate] = useState("");
  const [collecting, setCollecting] = useState(false);
  // v1.60.3: Idempotency-Key per il dialog "Registra incasso". Rigenerata
  // ad ogni apertura (openCollect), invariata attraverso i retry.
  const [collectIdemKey, setCollectIdemKey] = useState(() => newIdempotencyKey());
  // v1.59.1 ck14.2.b: Riferimento Documento strutturato sul modal Registra incasso.
  const [collectDoc, setCollectDoc] = useState({});
  // v1.60.1: rimosso `alsoDaily` (checkbox "Aggiungi anche alla Prima Nota
  // Incassi") e il secondo dialog `dailyDraft`. Motivo: la `Collection`
  // creata da "Registra incasso" appare AUTOMATICAMENTE in Prima Nota
  // (vista derivata su Payment+Collection+DailyReceipt); la seconda
  // registrazione produceva una DailyReceipt con linked_occurrence_id
  // che veniva ESCLUSA da Prima Nota → operazione ridondante e senza
  // effetto contabile. Il flusso ora è: un solo submit = un solo movimento.

  // Static lookups — fetched ONCE on mount. Filter changes don't touch them.
  useEffect(() => {
    (async () => {
      try {
        const [cl, ca, recv, pms] = await Promise.all([
          arListClients({ active_only: true }),
          arListCategories({ active_only: true }),
          arListReceivables({ limit: 1000 }),
          listPaymentMethods({ includeArchived: false }),
        ]);
        setClients(cl || []);
        setCategories(ca || []);
        setReceivables(recv || []);
        setPaymentMethods(pms || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const [reloadKey, setReloadKey] = useState(0);
  const load = () => {
    setLoading(true);
    setReloadKey((k) => k + 1);
  };

  // Iter42: aggiornamento puntuale di una singola riga per evitare il refetch
  // globale dopo azioni mirate (Collection, NotDue, Restore). Su dataset
  // significativi questo è il principale acceleratore della UX durante la
  // registrazione incassi.
  const mergeRow = (updated) => {
    if (!updated || !updated.id) return;
    setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only the occurrences depend on filters; lookups are static (see above).
        const occ = await arListOccurrences({
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          client_id: filters.client_id || undefined,
          category_id: filters.category_id || undefined,
          status: filters.status || undefined,
          limit: 1000,
        });
        if (cancelled) return;
        setRows(occ || []);
      } catch (e) {
        if (!cancelled) toast.error("Errore caricamento incassi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    filters.date_from,
    filters.date_to,
    filters.client_id,
    filters.category_id,
    filters.status,
    reloadKey,
  ]);

  const recvMap = useMemo(
    () => Object.fromEntries(receivables.map((r) => [r.id, r])),
    [receivables],
  );
  const cliMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients],
  );
  const catMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  );

  // Debounce la ricerca testuale (300ms): senza questo, ogni carattere
  // digitato nel campo "Cerca" costringe React a rieseguire il
  // `filtered` useMemo sull'intera lista e a ri-renderizzare tabella,
  // KPI e footer. Su dataset grandi = lag di digitazione.
  const debouncedQ = useDebouncedValue(filters.q, 300);

  // Etichette (Fase 2): filtro OR/AND + batch fetch dei tag sulle occurrence
  // AR. La stessa entity_type ("receivable_occurrence") è usata da Prima Nota
  // per le righe di tipo "collection" → l'etichetta è condivisa tra vista
  // pianificata (scadenza) e vista consuntiva (Prima Nota).
  const [tagFilter, setTagFilter] = useState({ selectedIds: [], mode: "or", untaggedOnly: false });
  const occurrenceIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const { tagsByEntity, refetch: refetchTags } = useEntityTags(
    "receivable_occurrence",
    occurrenceIds,
  );

  // v1.59.1 ck14.2.b: mappa tipi documento
  const documentTypes = useDocumentTypes(true);
  const documentTypesById = useMemo(() => {
    const m = {};
    for (const t of documentTypes) m[t.id] = t;
    return m;
  }, [documentTypes]);

  const filtered = useMemo(() => {
    let list = rows;
    // v1.60.4 — di default nascondi le scadenze già chiuse. Esclude:
    //   collected, defaulted, cancelled, not_due
    // Se l'utente ha filtrato esplicitamente su uno stato "chiuso", NON
    // applichiamo il taglio (rispetta l'intenzione dell'utente).
    const CLOSED = new Set(["collected", "defaulted", "cancelled", "not_due"]);
    if (!showCollected && !CLOSED.has(filters.status)) {
      list = list.filter((r) => !CLOSED.has(r.status));
    }
    if (filters.kind === "ordinary") {
      list = list.filter((r) => recvMap[r.receivable_id]?.recurrence_type === "one_time");
    } else if (filters.kind === "recurring") {
      list = list.filter((r) => recvMap[r.receivable_id]?.recurrence_type !== "one_time");
    }
    if (debouncedQ) {
      const q = debouncedQ.toLowerCase();
      list = list.filter((r) => {
        const recv = recvMap[r.receivable_id];
        if (!recv) return false;
        return (
          (recv.title || "").toLowerCase().includes(q) ||
          (r.invoice_reference || "").toLowerCase().includes(q) ||
          (cliMap[r.effective_client_id]?.name || "").toLowerCase().includes(q)
        );
      });
    }
    if (tagFilter.untaggedOnly) {
      list = list.filter((r) => (tagsByEntity[r.id] || []).length === 0);
    } else if (tagFilter.selectedIds.length > 0) {
      list = list.filter((r) => {
        const rowTagIds = new Set((tagsByEntity[r.id] || []).map((t) => t.id));
        if (tagFilter.mode === "and") {
          return tagFilter.selectedIds.every((id) => rowTagIds.has(id));
        }
        return tagFilter.selectedIds.some((id) => rowTagIds.has(id));
      });
    }
    return list;
  }, [rows, filters.kind, filters.status, debouncedQ, recvMap, cliMap, tagFilter, tagsByEntity, showCollected]);

  // Conteggio delle scadenze chiuse (incassate/annullate/non dovute)
  // nascoste per informare l'utente del taglio applicato.
  const closedHiddenCount = useMemo(() => {
    const CLOSED = new Set(["collected", "defaulted", "cancelled", "not_due"]);
    if (showCollected || CLOSED.has(filters.status)) return 0;
    return rows.filter((r) => CLOSED.has(r.status)).length;
  }, [rows, showCollected, filters.status]);

  const totals = useMemo(() => {
    let planned = 0;
    let collected = 0;
    let count = 0;
    let openCount = 0;
    let overdueAmount = 0;
    const todayIso = isoLocalDate(new Date());
    for (const r of filtered) {
      if (r.status === "cancelled" || r.status === "defaulted" || r.status === "not_due") continue;
      planned += num(r.effective_amount);
      collected += num(r.total_collected);
      count += 1;
      if (r.status === "due" || r.status === "partially_collected") openCount += 1;
      if (
        (r.status === "due" || r.status === "partially_collected") &&
        r.due_date < todayIso
      ) {
        overdueAmount += num(r.effective_amount) - num(r.total_collected);
      }
    }
    return {
      planned,
      collected,
      remaining: planned - collected,
      count,
      openCount,
      overdueAmount,
    };
  }, [filtered]);

  const setField = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  /* --- Quick collect ------------------------------------------------- */
  const openCollect = (row) => {
    const remaining = num(row.effective_amount) - num(row.total_collected);
    setCollectRow(row);
    // v1.60.3: nuova operazione = nuova chiave idempotenza.
    setCollectIdemKey(newIdempotencyKey());
    setCollectAmount(remaining > 0 ? remaining.toFixed(2) : Number(row.effective_amount).toFixed(2));
    setCollectDate(isoLocalDate(new Date()));
    // v1.60.4 — priorità metodo proposto:
    //   1. Ricorrenza (Receivable.default_payment_method_id)
    //   2. Cliente (Client.default_payment_method_id)
    //   3. Default globale "Bonifico"
    // Il metodo è memorizzato come FK a payment_method_defs; qui lo
    // mappiamo all'enum CollectionMethod tramite il nome (case+accent
    // insensitive). Se il PaymentMethodDef è stato archiviato o non
    // risulta mappabile → fallback bank_transfer (invariato).
    let proposedMethod = "bank_transfer";
    const recv = recvMap[row.receivable_id];
    const client = cliMap[row.effective_client_id];
    const preferredPmId =
      recv?.default_payment_method_id || client?.default_payment_method_id || null;
    if (preferredPmId) {
      const pm = paymentMethods.find((p) => p.id === preferredPmId);
      if (pm) {
        const mapped = pmNameToCollectionMethod(pm.name);
        if (mapped) proposedMethod = mapped;
      }
    }
    setCollectMethod(proposedMethod);
    setCollectRef("");
    // Iter41: precarica i campi fattura se la scadenza ne ha già (ordinario
    // creato con i dati o incasso parziale precedente).
    setCollectInvoiceRef(row.invoice_reference || "");
    setCollectInvoiceDate(row.invoiced_at || "");
    // v1.59.1 ck14.2.b: eredita i doc_* dall'occurrence come default.
    setCollectDoc({
      doc_type_id: row.doc_type_id || null,
      doc_number: row.doc_number || "",
      doc_date: row.doc_date || row.invoiced_at || "",
      doc_protocol: row.doc_protocol || "",
      doc_notes: "",
    });
  };
  const closeCollect = () => {
    setCollectRow(null);
    setCollecting(false);
  };
  const submitCollect = async () => {
    if (!collectRow || collecting) return;
    if (!collectAmount || Number(collectAmount) <= 0) {
      toast.error("Importo non valido");
      return;
    }
    setCollecting(true);
    try {
      await arCreateCollection({
        occurrence_id: collectRow.id,
        collected_amount: String(collectAmount),
        collected_at: collectDate,
        method: collectMethod,
        reference: collectRef || null,
        invoice_reference: collectInvoiceRef.trim() || null,
        invoiced_at: collectInvoiceDate || null,
        doc_type_id: collectDoc.doc_type_id || null,
        doc_number: collectDoc.doc_number || null,
        doc_date: collectDoc.doc_date || null,
        doc_protocol: collectDoc.doc_protocol || null,
        doc_notes: collectDoc.doc_notes || null,
      }, { idempotencyKey: collectIdemKey });
      toast.success("Incasso registrato.");
      // Snapshot before clearing state so we can update the row.
      const row = collectRow;
      closeCollect();
      // v1.60.3: consuma la chiave usata.
      setCollectIdemKey(newIdempotencyKey());
      // Iter42: aggiornamento puntuale invece di reload completo della lista.
      try {
        const updated = await arGetOccurrence(row.id);
        mergeRow(updated);
      } catch {
        /* best-effort: la riga si aggiorna al prossimo refetch */
      }
    } catch (e) {
      setCollecting(false);
      if (e?.isIdempotencyConflict) {
        toast.error(e.message);
      } else {
        toast.error(formatApiError(e, "Errore registrazione incasso"));
      }
    }
  };

  /* --- Cancel/skip occurrence --------------------------------------- */
  const cancelOcc = async (row) => {
    if (!window.confirm(`Annullare la scadenza del ${fmtDate(row.due_date)}?`)) return;
    try {
      const updated = await arCancelOccurrence(row.id);
      toast.success("Scadenza annullata.");
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore annullamento"));
    }
  };

  /* --- Mark "Non dovuto" / Restore (storno amministrativo) ---------- */
  const [notDueTarget, setNotDueTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const openMarkNotDue = (row) => setNotDueTarget(row);
  const confirmMarkNotDue = async () => {
    if (!notDueTarget) return;
    try {
      const updated = await arMarkOccurrenceNotDue(notDueTarget.id);
      toast.success("Incasso segnato come non dovuto.");
      setNotDueTarget(null);
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const openRestore = (row) => setRestoreTarget(row);
  const confirmRestore = async () => {
    if (!restoreTarget) return;
    try {
      const updated = await arRestoreOccurrence(restoreTarget.id);
      toast.success("Incasso ripristinato come atteso.");
      setRestoreTarget(null);
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const todayIso = isoLocalDate(new Date());

  // UI/UX — collapsible filter panel with persisted state (densità operativa)
  const [filtersOpen, setFiltersOpen] = usePersistedBoolean(
    "receivables:filtersOpen",
    true,
  );

  return (
    <div data-testid="receivables-page">
      <PageHeader
        testId="receivables-header"
        title="Scadenze incassi"
        subtitle="Gestione incassi clienti"
        actions={
          <>
            <Button asChild variant="outline" data-testid="btn-list-recurring" size="sm" className="h-8 text-xs">
              <Link to="/receivables/recurring">Incassi ricorrenti</Link>
            </Button>
            <Button asChild variant="outline" data-testid="btn-list-ordinary" size="sm" className="h-8 text-xs">
              <Link to="/receivables/daily">Incassi ordinari</Link>
            </Button>
            <Button asChild data-testid="btn-new-receivable" size="sm" className="h-8 text-xs">
              <Link to="/receivables/new">+ Nuovo incasso</Link>
            </Button>
          </>
        }
      />

      {/* KPI strip (compact horizontal) */}
      <section
        data-testid="receivables-kpi"
        className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4"
      >
        <Tile label="Previsto incassi" value={EUR(totals.planned)} testId="kpi-planned" />
        <Tile label="Incassato" value={EUR(totals.collected)} testId="kpi-collected" tone="emerald" />
        <Tile
          label="Da incassare"
          value={EUR(totals.remaining)}
          testId="kpi-remaining"
          tone={totals.remaining > 0 ? "amber" : "stone"}
        />
        <Tile
          label="Scaduto da incassare"
          value={EUR(totals.overdueAmount)}
          testId="kpi-overdue"
          tone={totals.overdueAmount > 0 ? "rose" : "stone"}
        />
      </section>

      {/* Filters */}
      <div
        data-testid="receivables-presets"
        className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs text-stone-500"
      >
        <span className="font-medium uppercase tracking-wider text-[10px]">Periodo</span>
        <PeriodPicker
          value={{ from: filters.date_from, to: filters.date_to }}
          onChange={(from, to) =>
            setFilters((s) => ({ ...s, date_from: from, date_to: to }))
          }
          testId="rec-period"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="preset-month"
          onClick={() => {
            const [f, t] = monthBounds(new Date());
            setFilters((s) => ({ ...s, date_from: f, date_to: t }));
          }}
          className="h-7 px-2 text-xs"
        >
          Mese corrente
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="preset-year"
          onClick={() => {
            const [f, t] = yearBounds(new Date());
            setFilters((s) => ({ ...s, date_from: f, date_to: t }));
          }}
          className="h-7 px-2 text-xs"
        >
          Anno corrente
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="preset-all"
          onClick={() =>
            setFilters((s) => ({ ...s, date_from: "", date_to: "" }))
          }
          className="h-7 px-2 text-xs"
        >
          Tutti
        </Button>
        <label
          data-testid="toggle-show-collected"
          className="flex cursor-pointer items-center gap-1.5 rounded-md border border-stone-200 bg-stone-50 px-2 py-1 text-[11px] text-stone-600 hover:bg-stone-100"
          title="Se attivo, mostra anche le scadenze già incassate/annullate/non dovute."
        >
          <input
            type="checkbox"
            checked={showCollected}
            onChange={(e) => setShowCollected(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Mostra incassati
          {closedHiddenCount > 0 && !showCollected && (
            <span
              data-testid="closed-hidden-badge"
              className="rounded-full bg-stone-200 px-1.5 py-0.5 text-[9px] font-medium tabular-nums text-stone-600"
            >
              {closedHiddenCount} nascosti
            </span>
          )}
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="toggle-filters"
          onClick={() => setFiltersOpen((v) => !v)}
          className="ml-auto h-7 px-2 text-xs"
          title={filtersOpen ? "Nascondi filtri avanzati" : "Mostra filtri avanzati"}
        >
          <SlidersHorizontal size={12} className="mr-1" />
          Filtri
          {filtersOpen ? (
            <ChevronDown size={12} className="ml-1" />
          ) : (
            <ChevronRight size={12} className="ml-1" />
          )}
        </Button>
        <TagFilter
          selectedIds={tagFilter.selectedIds}
          mode={tagFilter.mode}
          untaggedOnly={tagFilter.untaggedOnly}
          onChange={setTagFilter}
          testIdPrefix="receivables-tag-filter"
        />
      </div>

      {filtersOpen && (
      <section
        data-testid="receivables-filters"
        className="mb-3 grid grid-cols-1 gap-3 rounded-xl border border-stone-200 bg-white p-3 md:grid-cols-7"
      >
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Cerca</Label>
          <Input
            data-testid="filter-search"
            placeholder="Oggetto, cliente, n. fattura"
            value={filters.q}
            onChange={(e) => setField("q", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Dal</Label>
          <Input
            type="date"
            data-testid="filter-from"
            value={filters.date_from}
            onChange={(e) => setField("date_from", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Al</Label>
          <Input
            type="date"
            data-testid="filter-to"
            value={filters.date_to}
            onChange={(e) => setField("date_to", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Cliente</Label>
          <Select
            value={filters.client_id || "all"}
            onValueChange={(v) => setField("client_id", v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-client">
              <SelectValue placeholder="Tutti i clienti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Categoria</Label>
          <Select
            value={filters.category_id || "all"}
            onValueChange={(v) => setField("category_id", v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-category">
              <SelectValue placeholder="Tutte le categorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Stato</Label>
          <Select
            value={filters.status || "all"}
            onValueChange={(v) => setField("status", v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {Object.entries(arStatusIT).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-7">
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Tipo</Label>
          <div className="mt-1 flex gap-2">
            {[
              { value: "all", label: "Tutti" },
              { value: "ordinary", label: "Ordinari" },
              { value: "recurring", label: "Ricorrenti" },
            ].map((opt) => (
              <Button
                key={opt.value}
                type="button"
                size="sm"
                variant={filters.kind === opt.value ? "default" : "outline"}
                data-testid={`filter-kind-${opt.value}`}
                onClick={() => setField("kind", opt.value)}
                className="h-8 text-xs"
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Table */}
      <section className="rounded-xl border border-stone-200 bg-white">
        <Table data-testid="receivables-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Scadenza</TableHead>
              <TableHead className="w-[150px]">N. Fattura</TableHead>
              <TableHead className="w-[110px]">Data fattura</TableHead>
              <TableHead>Cliente · Oggetto</TableHead>
              <TableHead className="w-[120px]">Categoria</TableHead>
              <TableHead className="w-[120px]">Stato</TableHead>
              <TableHead className="w-[110px] text-right">Previsto</TableHead>
              <TableHead className="w-[110px] text-right">Incassato</TableHead>
              <TableHead className="w-[110px] text-right">Residuo</TableHead>
              <TableHead className="w-[200px]">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-sm text-stone-500">
                  {loading ? "Caricamento…" : "Nessuna scadenza incasso nel periodo"}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => {
              const recv = recvMap[r.receivable_id];
              const remaining = num(r.effective_amount) - num(r.total_collected);
              const overdue =
                (r.status === "due" || r.status === "partially_collected") &&
                r.due_date < todayIso;
              const cliName = cliMap[r.effective_client_id]?.name || "—";
              const catName = catMap[r.effective_category_id]?.name || "—";
              return (
                <TableRow
                  key={r.id}
                  data-testid={`receivables-row-${r.id}`}
                  className="[&>td]:py-1.5"
                >
                  <TableCell
                    className={
                      "font-mono text-xs tabular-nums " +
                      (overdue ? "font-semibold text-rose-700" : "text-stone-700")
                    }
                  >
                    {fmtDate(r.due_date)}
                  </TableCell>
                  <TableCell
                    data-testid={`row-invoice-ref-${r.id}`}
                    className="text-xs text-stone-700"
                  >
                    {r.invoice_reference ? (
                      <span title={r.invoice_reference} className="line-clamp-2">
                        {r.invoice_reference}
                      </span>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </TableCell>
                  <TableCell
                    data-testid={`row-invoice-date-${r.id}`}
                    className="font-mono text-xs tabular-nums text-stone-500"
                  >
                    {r.invoiced_at ? fmtDate(r.invoiced_at) : <span className="text-stone-300">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        {/* Iter41: gerarchia invertita — CLIENTE come riga
                            principale in grassetto, OGGETTO come riga
                            secondaria. Più operativo nell'uso reale. */}
                        <div className="text-sm font-semibold text-stone-900 truncate">
                          {cliName || "—"}
                        </div>
                        <div className="truncate text-[11px] text-stone-500">
                          {recv?.title || "—"}
                        </div>
                        {/* Fase 2 etichette: chip inline + popover */}
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <TagList
                            tags={tagsByEntity[r.id] || []}
                            size="sm"
                            max={4}
                            onRemove={async (t) => {
                              try {
                                await (await import("@/lib/api")).detachTag(
                                  t.id,
                                  "receivable_occurrence",
                                  r.id,
                                );
                                refetchTags();
                              } catch {
                                /* silent */
                              }
                            }}
                          />
                          <TagAssignmentPopover
                            entityType="receivable_occurrence"
                            entityId={r.id}
                            currentTagIds={(tagsByEntity[r.id] || []).map((t) => t.id)}
                            onChange={() => refetchTags()}
                            testIdPrefix={`receivable-tag-${r.id}`}
                          />
                          {(r.doc_type_id || r.doc_number) && (
                            <DocumentBadge
                              type={documentTypesById[r.doc_type_id]}
                              number={r.doc_number}
                              date={r.doc_date}
                              testId={`doc-badge-recv-${r.id}`}
                            />
                          )}
                        </div>
                      </div>
                      {r.linked_daily_receipts_count > 0 && (
                        <Link
                          to={`/receivables/daily?occ=${r.id}`}
                          data-testid={`linked-daily-badge-${r.id}`}
                          title={`${r.linked_daily_receipts_count} riga/righe Prima Nota — totale ${EUR(r.linked_daily_receipts_total || 0)}`}
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100"
                        >
                          <Link2 size={10} />
                          {r.linked_daily_receipts_count} · {EUR(r.linked_daily_receipts_total || 0)}
                        </Link>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-stone-600">{catName}</TableCell>
                  <TableCell>
                    <span
                      className={
                        "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                        (overdue
                          ? "bg-rose-600 text-white"
                          : r.status === "collected"
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            : r.status === "partially_collected"
                              ? "border border-amber-200 bg-amber-50 text-amber-800"
                              : r.status === "defaulted"
                                ? "border border-rose-200 bg-rose-50 text-rose-700"
                                : r.status === "cancelled"
                                  ? "border border-stone-200 bg-stone-50 text-stone-500"
                                  : r.status === "not_due"
                                    ? "border border-slate-300 bg-slate-50 text-slate-600"
                                    : "border border-blue-200 bg-blue-50 text-blue-700")
                      }
                    >
                      {overdue ? "Scaduto" : arStatusIT[r.status] || r.status}
                    </span>
                    {r.status === "collected" && r.last_collection_at && (
                      <div
                        data-testid={`collected-date-${r.id}`}
                        className="mt-0.5 font-mono text-[10px] leading-tight tabular-nums text-stone-500"
                      >
                        il {fmtDate(r.last_collection_at)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm tabular-nums">
                    {EUR(r.effective_amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm tabular-nums text-emerald-700">
                    {EUR(r.total_collected || 0)}
                  </TableCell>
                  <TableCell
                    className={
                      "text-right font-mono text-sm font-semibold tabular-nums " +
                      (remaining > 0 ? "text-amber-900" : "text-stone-500")
                    }
                  >
                    {EUR(remaining)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.status !== "collected" &&
                        r.status !== "cancelled" &&
                        r.status !== "not_due" && (
                          <Button
                            size="sm"
                            data-testid={`btn-collect-${r.id}`}
                            onClick={() => openCollect(r)}
                            className="h-7 px-2 text-xs"
                          >
                            Registra incasso
                          </Button>
                        )}
                      {r.status !== "cancelled" && r.status !== "not_due" && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`btn-cancel-${r.id}`}
                          onClick={() => cancelOcc(r)}
                          className="h-7 px-2 text-xs"
                          title="Annulla scadenza"
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                      {(r.status === "due" || r.status === "defaulted") &&
                        Number(r.total_collected || 0) === 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`btn-not-due-${r.id}`}
                            onClick={() => openMarkNotDue(r)}
                            className="h-7 px-2 text-xs"
                            title="Segna come non dovuto (storno amministrativo)"
                          >
                            <Ban size={12} />
                          </Button>
                        )}
                      {(r.status === "cancelled" || r.status === "not_due") && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`btn-restore-${r.id}`}
                          onClick={() => openRestore(r)}
                          className="h-7 px-2 text-xs"
                          title="Ripristina come atteso"
                        >
                          <RotateCcw size={12} />
                        </Button>
                      )}
                      {Number(r.total_collected || 0) > 0 && (
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          data-testid={`btn-prima-nota-${r.id}`}
                          className="h-7 px-2 text-xs"
                          title="Vai in Prima Nota — movimenti reali di questa scadenza"
                        >
                          <Link to={`/prima-nota?occurrence_id=${r.id}`}>
                            <BookOpen size={12} />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </section>

      {/* Collect dialog */}
      <Dialog open={!!collectRow} onOpenChange={(o) => { if (!o) closeCollect(); }}>
        <DialogContent data-testid="dialog-collect">
          <DialogHeader>
            <DialogTitle>Registra incasso</DialogTitle>
            <DialogDescription>
              Anche incassi parziali sono supportati. Lo stato si aggiorna automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Importo</Label>
              <StableInput
                type="number"
                step="0.01"
                data-testid="input-collect-amount"
                value={collectAmount}
                onCommit={setCollectAmount}
              />
              {collectRow && (
                <p className="mt-1 text-xs text-stone-500">
                  Previsto {EUR(collectRow.effective_amount)} · già incassato {EUR(collectRow.total_collected || 0)}
                </p>
              )}
            </div>
            <div>
              <Label>Data incasso</Label>
              <Input
                type="date"
                data-testid="input-collect-date"
                value={collectDate}
                onChange={(e) => setCollectDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Metodo di incasso</Label>
              <Select value={collectMethod} onValueChange={setCollectMethod}>
                <SelectTrigger data-testid="select-collect-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {arMethodOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Riferimento (es. CRO, contabile bancaria)</Label>
              <StableInput
                data-testid="input-collect-ref"
                value={collectRef}
                onCommit={setCollectRef}
                placeholder="Riferimento bancario interno (opzionale)"
              />
            </div>
            <div className="rounded-md border border-stone-200 bg-stone-50/40 p-2.5">
              <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-stone-600">
                Documento incassato
              </div>
              <div className="grid gap-2 md:grid-cols-[2fr_1fr]">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                    N. Fattura
                  </Label>
                  <StableInput
                    data-testid="input-collect-invoice-ref"
                    value={collectInvoiceRef}
                    onCommit={setCollectInvoiceRef}
                    placeholder="Es. Fatt. 119A · Saldo fatt. 12 e 15 · Acconto pratica Rossi"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                    Data fattura
                  </Label>
                  <Input
                    type="date"
                    data-testid="input-collect-invoice-date"
                    value={collectInvoiceDate}
                    onChange={(e) => setCollectInvoiceDate(e.target.value)}
                  />
                </div>
              </div>
              <p className="mt-1 text-[10px] leading-snug text-stone-500">
                Testo libero: puoi indicare più fatture/riferimenti.
                I dati vengono associati alla scadenza, non al singolo
                incasso parziale.
              </p>
            </div>
            <DocumentReferenceFields
              value={collectDoc}
              onChange={setCollectDoc}
              defaultOpen={!!(collectDoc.doc_type_id || collectDoc.doc_number)}
              testIdPrefix="collect-docref"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCollect} disabled={collecting}>
              Annulla
            </Button>
            <Button
              data-testid="btn-confirm-collect"
              onClick={submitCollect}
              disabled={collecting}
            >
              {collecting ? "Registrazione…" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.60.1: rimosso "Conferma riga Prima Nota Incassi".
          La Collection registrata sopra appare automaticamente in Prima
          Nota (vista derivata da Payment+Collection+DailyReceipt). */}

      <AlertDialog
        open={!!notDueTarget}
        onOpenChange={(o) => !o && setNotDueTarget(null)}
      >
        <AlertDialogContent data-testid="dialog-ar-not-due">
          <AlertDialogHeader>
            <AlertDialogTitle>Segnare l&apos;incasso come &quot;Non dovuto&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Usa questa azione quando l&apos;incasso <strong>non sarà mai
              riscosso</strong> (es. nota di credito, contratto risolto
              prima dell&apos;emissione, importo compensato). La scadenza sparirà
              dagli elenchi operativi ma resta consultabile nello storico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-not-due"
              onClick={confirmMarkNotDue}
            >
              Segna come non dovuto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!restoreTarget}
        onOpenChange={(o) => !o && setRestoreTarget(null)}
      >
        <AlertDialogContent data-testid="dialog-ar-restore">
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristinare l&apos;incasso?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;incasso tornerà allo stato <em>In attesa</em> e ricomparirà
              negli elenchi operativi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-restore"
              onClick={confirmRestore}
            >
              Ripristina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Tile({ label, value, tone = "stone", testId }) {
  const tones = {
    stone: "border-stone-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-900",
    amber: "border-amber-200 bg-amber-50/40 text-amber-900",
    rose: "border-rose-200 bg-rose-50/40 text-rose-900",
  };
  return (
    <div
      data-testid={testId}
      className={
        "flex min-w-0 flex-col gap-0.5 rounded-lg border px-3 py-2 " +
        (tones[tone] || tones.stone)
      }
    >
      <div className="truncate text-[10px] uppercase tracking-wider text-stone-500">
        {label}
      </div>
      <div
        className="truncate font-mono text-base font-semibold tabular-nums"
        title={value}
      >
        {value}
      </div>
    </div>
  );
}
